import { useEffect, useState } from 'react'
import { createFileRoute, Link } from '@tanstack/react-router'
import { TextField } from '@bloo-team/ui'
import { isProvisioned } from '@bloo-team/api-client'
import { Pagination } from '@bloo-team/ui'
import { useTenants } from '../../../lib/tenants'

/**
 * Settings · Tenants (docs/settings-requirements.md §7).
 *
 * A tenant is a CLUSTER — these rows are the deployments in the rail's context
 * switcher. The API says cluster, the product says tenant.
 *
 * The columns are the whole point: `Primary` and `Secondary` are the
 * organization↔tenant relationship, denormalised onto the row. The
 * Organizations section shows the same edge from the other end and cannot
 * change it; this is where it is written, on each tenant's own page.
 *
 * `state` is a PROVISIONING lifecycle, not an enabled flag. A tenant whose
 * setup never finished is a real row that this console cannot open — it lists
 * it and says so, rather than linking to a page that would fail.
 */

function TenantsPage() {
  const { clusterId, scopeId } = Route.useParams()
  const [search, setSearch] = useState('')
  const [query, setQuery] = useState('')
  const [page, setPage] = useState(1)
  const [size, setSize] = useState(50)

  useEffect(() => {
    const t = setTimeout(() => { setQuery(search); setPage(1) }, 250)
    return () => clearTimeout(t)
  }, [search])

  const list = useTenants({ page, size, query, filters: [] })
  const rows = list.data?.tenants ?? []
  const total = list.data?.total ?? 0

  return (
    <div className="app-stack app-stack-wide">
      <section className="hud-panel sy-card" aria-label="Tenants">
        <div className="sy-pane-head">
          <span className="hud-eyebrow">Tenants</span>
          <span className="mono muted">{total}</span>
        </div>

        <div className="sy-toolbar">
          <TextField
            aria-label="Search tenants"
            value={search}
            onChange={setSearch}
            placeholder="Search by name…"
          />
        </div>

        {list.error ? <p className="sy-error" role="alert">{list.error.message}</p> : null}

        {!rows.length && !list.isLoading && !list.error ? (
          <p className="sy-help">
            {query ? `No tenant matches “${query}”.` : 'No tenants in this deployment.'}
          </p>
        ) : null}

        {rows.length ? (
          <div className="sy-table-wrap">
            <table className="dense-table">
              <thead>
                <tr>
                  <th>Tenant</th>
                  <th>Licence</th>
                  <th>Primary organization</th>
                  <th>Secondary organizations</th>
                  <th>State</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((t) => {
                  const ready = isProvisioned(t)
                  return (
                    <tr key={t.clusterId}>
                      <td>
                        {ready ? (
                          <Link
                            to="/$clusterId/$scopeId/settings/tenants/$tenantId"
                            params={{ clusterId, scopeId, tenantId: t.clusterId }}
                            className="cell-name"
                          >
                            {t.clusterName || <span className="muted">no name</span>}
                          </Link>
                        ) : (
                          // Not a link: the detail page reads a tenant that
                          // finished being set up, and finishing setup is the
                          // one thing this console cannot do.
                          <span>{t.clusterName || <span className="muted">no name</span>}</span>
                        )}
                      </td>
                      <td>{t.licenseType || <span className="muted">—</span>}</td>
                      <td>
                        {t.primaryOrgName || <span className="muted">none</span>}
                      </td>
                      <td>
                        {t.secondaryOrg.length
                          ? t.secondaryOrg.map((o) => o.orgName).join(', ')
                          : <span className="muted">none</span>}
                      </td>
                      <td>
                        <span className="sy-pill" data-tone={ready ? 'good' : 'warn'}>
                          {t.state || 'unknown'}
                        </span>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : null}

        {total > 0 ? (
          <Pagination
            label="Tenant pagination"
            page={page}
            pageSize={size}
            total={total}
            busy={list.isFetching}
            unit="tenants"
            onPageChange={setPage}
            onPageSizeChange={(n) => { setSize(n); setPage(1) }}
          />
        ) : null}

        <p className="sy-help">
          Open a tenant to change which organizations reach it. A tenant has one primary
          organization and any number of secondary ones; the primary is set by the first
          organization added and cannot be swapped afterwards.
        </p>
        <p className="sy-help">
          A tenant whose state is not configured, connected or validated has not finished being
          set up. It is listed but cannot be opened — completing setup provisions infrastructure,
          which this console does not do.
        </p>
      </section>
    </div>
  )
}

export const Route = createFileRoute('/$clusterId/$scopeId/settings/tenants')({
  component: TenantsPage,
})
